import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

// Every length is a multiple of the width `--phone-w`, in an iPhone 15 Pro's real proportions (71.5 × 147.6 × 8.25 mm),
// so the body keeps its shape at whatever width its slot sets.
const k = (ratio: number) => `calc(var(--phone-w) * ${ratio})`;
const W = k(1);
const H = k(2.064);
const DEPTH = k(0.1154);
const RADIUS = k(0.155);
const BEZEL = k(0.027);
const BUTTON_PROUD = k(0.009);

// Trigonometry is rounded before it reaches the markup: the server (JavaScriptCore) and the browser may disagree in the
// last digits, and a hydration mismatch on a style is still a mismatch.
const fixed = (n: number) => Number(n.toFixed(4));

type Strip = { cx: string; cy: string; angle: number; length: string };

// A style that also carries the face's normal as custom properties, which `CSSProperties` has no key for.
type LitStyle = CSSProperties & Partial<Record<`--n${'x' | 'y' | 'z'}`, number>>;

// Must match `phone-settle` in -styles/app-phone.css: faces are culled against it. Turns left, toward the copy.
const TURN = { from: { rx: 4, ry: -42 }, to: { rx: 12, ry: -20 } };

/**
 * Whether the face ever turns toward the reader during `TURN`. One that never does is not rendered: every face is a
 * layer depth-sorted on each frame, which a phone feels. The margin keeps faces the perspective shows at a grazing angle.
 */
function facesReader(nx: number, ny: number, nz = 0) {
	const margin = 0.09 * Math.abs(nx) + 0.18 * Math.abs(ny) + 0.02;
	for (let step = 0; step <= 10; step++) {
		const along = (from: number, to: number) => ((from + (to - from) * (step / 10)) * Math.PI) / 180;
		const rx = along(TURN.from.rx, TURN.to.rx);
		const ry = along(TURN.from.ry, TURN.to.ry);
		if (ny * Math.sin(rx) + (nz * Math.cos(ry) - nx * Math.sin(ry)) * Math.cos(rx) > -margin) return true;
	}
	return false;
}

/**
 * A rounded rectangle extruded along Z: four sides plus `facets` strips per corner, tangent to the arc so neighbours
 * share an edge. Each carries its normal so `phone-lit` shades it by the current rotation.
 */
function Wall(props: {
	x: string;
	y: string;
	w: string;
	h: string;
	r: string;
	depth: string;
	z: string;
	facets: number;
	className: string;
	angles?: number[];
}) {
	const { x, y, w, h, r, facets } = props;
	const right = `calc(${x} + ${w} - ${r})`;
	const left = `calc(${x} + ${r})`;
	const top = `calc(${y} + ${r})`;
	const bottom = `calc(${y} + ${h} - ${r})`;
	const strips: Strip[] = [
		{ cx: right, cy: `calc(${y} + ${h} / 2)`, angle: 0, length: `calc(${h} - 2 * ${r} + 1px)` },
		{ cx: `calc(${x} + ${w} / 2)`, cy: bottom, angle: 90, length: `calc(${w} - 2 * ${r} + 1px)` },
		{ cx: left, cy: `calc(${y} + ${h} / 2)`, angle: 180, length: `calc(${h} - 2 * ${r} + 1px)` },
		{ cx: `calc(${x} + ${w} / 2)`, cy: top, angle: 270, length: `calc(${w} - 2 * ${r} + 1px)` },
	];
	if (facets > 0) {
		const step = 90 / facets;
		// A hair of overlap so antialiasing never opens a seam between neighbouring strips.
		const length = `calc(${r} * ${fixed(2 * Math.tan(((step / 2) * Math.PI) / 180))} + 0.6px)`;
		const corners: [string, string, number][] = [
			[right, bottom, 0],
			[left, bottom, 90],
			[left, top, 180],
			[right, top, 270],
		];
		for (const [cx, cy, start] of corners) {
			for (let i = 0; i < facets; i++) strips.push({ cx, cy, angle: fixed(start + step * (i + 0.5)), length });
		}
	}

	const shown = strips.filter((strip) => {
		const rad = (strip.angle * Math.PI) / 180;
		return (!props.angles || props.angles.includes(strip.angle)) && facesReader(Math.cos(rad), Math.sin(rad));
	});

	return shown.map((strip) => {
		const rad = (strip.angle * Math.PI) / 180;
		const style: LitStyle = {
			'left': strip.cx,
			'top': strip.cy,
			'width': props.depth,
			'height': strip.length,
			'marginLeft': `calc(${props.depth} / -2)`,
			'marginTop': `calc(${strip.length} / -2)`,
			'transform': `translateZ(${props.z}) rotate(${strip.angle}deg) translateX(${r}) rotateY(90deg)`,
			'--nx': fixed(Math.cos(rad)),
			'--ny': fixed(Math.sin(rad)),
		};
		return (
			<span
				key={strip.angle}
				aria-hidden
				className={cn('phone-lit absolute backface-hidden', props.className)}
				style={style}
			/>
		);
	});
}

/** Outer face and front cap only: the phone never turns its back, and the ends are slivers not worth a layer. */
function SideButton(props: { side: 'left' | 'right'; top: number; length: number }) {
	const thickness = `calc(${DEPTH} * 0.42)`;
	const x = props.side === 'left' ? `calc(${BUTTON_PROUD} * -1)` : `calc(${W} - 2px)`;
	const w = `calc(${BUTTON_PROUD} + 2px)`;
	const top = k(2.064 * props.top);
	const height = k(2.064 * props.length);
	const cap: CSSProperties & Record<'--nz', number> = {
		'left': x,
		'top': top,
		'width': w,
		'height': height,
		'transform': `translateZ(calc(${thickness} / 2))`,
		'--nz': 1,
	};
	return (
		<>
			<Wall
				x={x}
				y={top}
				w={w}
				h={height}
				r="0px"
				depth={thickness}
				z="0px"
				facets={0}
				className="phone-band"
				angles={[props.side === 'left' ? 180 : 0]}
			/>
			<span aria-hidden className="phone-lit phone-band absolute rounded-[1px] backface-hidden" style={cap} />
		</>
	);
}

/**
 * An iPhone 15 Pro in CSS 3D, ported from the danchiano /new landing. It only turns a little (`phone-settle`), so there
 * is no back to model; the faces read `--phone-rx`/`--phone-ry` so the light stays put while it turns.
 */
export function IPhone(props: { label: string; children: ReactNode; className?: string }) {
	const half = `calc(${DEPTH} / 2)`;
	return (
		<div
			role="img"
			aria-label={props.label}
			className={cn('relative perspective-[1400px]', props.className)}
			style={{ width: W, height: H }}
		>
			<div className="phone-body absolute inset-0 transform-3d">
				<Wall x="0px" y="0px" w={W} h={H} r={RADIUS} depth={DEPTH} z="0px" facets={5} className="phone-band" />

				{/* Action button and volume on the left, the side button on the right (fractions of the height). */}
				<SideButton side="left" top={0.19} length={0.045} />
				<SideButton side="left" top={0.27} length={0.08} />
				<SideButton side="left" top={0.37} length={0.08} />
				<SideButton side="right" top={0.285} length={0.13} />

				{/* Front: black glass edge to edge, the screen inside an even bezel, the Dynamic Island, the glare on top. */}
				<div
					className="phone-front absolute inset-0 backface-hidden"
					style={{ borderRadius: RADIUS, transform: `translateZ(${half})` }}
				>
					{/* Black behind the screen, not white: the zoomed screen can fall a subpixel short of the edge, and
					    whatever is under it shows there as a light fringe against the bezel. */}
					<div
						className="absolute overflow-hidden bg-[#050505]"
						style={{ inset: BEZEL, borderRadius: `calc(${RADIUS} - ${BEZEL})` }}
					>
						{props.children}
						<span
							className="absolute left-1/2 z-10 -translate-x-1/2 rounded-full bg-black"
							style={{ top: k(0.03), width: k(0.3), height: k(0.085) }}
						/>
						<span className="absolute bottom-1.5 left-1/2 z-10 h-1 w-[36%] -translate-x-1/2 rounded-full bg-black/35" />
						<span className="phone-glare pointer-events-none absolute inset-0 z-20" />
						{/* The glass's inner edge, over everything: a dark hairline that eats the antialiased light pixels
						    the rounded clip leaves around the screen, so the screen melts into the bezel. */}
						<span className="pointer-events-none absolute inset-0 z-30 rounded-[inherit] shadow-[inset_0_0_0_2px_#050505,inset_0_0_3px_2px_rgb(0_0_0/0.35)]" />
					</div>
				</div>
			</div>
		</div>
	);
}
